import { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, LogOut } from "lucide-react";
import { createPortal } from "react-dom";
import { useAuth } from "@/hooks/useAuth";
import HibridoGamesLink from "@/components/HibridoGamesLink";
import logo from "@/assets/fpf3-logo-new.png";

import { getDesktopNavItems, getMobileNavItems } from "./navItems";

function UserMenu({ onClose }: { onClose?: () => void }) {
  const { user, role, signOut, loading } = useAuth();
  const navigate = useNavigate();

  if (loading || !user) return null;

  return (
    <div className="flex items-center gap-3 border-l border-border/50 pl-3">
      <button
        onClick={async () => {
          await signOut();
          onClose?.();
          navigate("/");
        }}
        className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-bold uppercase tracking-wider text-foreground/60 hover:text-destructive hover:bg-destructive/10 rounded-lg transition-all duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-destructive"
      >
        <LogOut size={14} />
        <span className="hidden sm:inline">Sair</span>
      </button>
    </div>
  );
}


function MobileMenu({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) {
  const location = useLocation();

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  return createPortal(
    <div
      className={`lg:hidden fixed inset-0 flex flex-col transition-all duration-500 ${
        isOpen ? "opacity-100 visible" : "opacity-0 invisible pointer-events-none"
      }`}
      style={{ zIndex: 99999 }}
    >
      <div
        className={`absolute inset-0 bg-black/60 backdrop-blur-sm transition-opacity duration-500 ${
          isOpen ? "opacity-100" : "opacity-0"
        }`}
        onClick={onClose}
      />
      <div
        className={`relative flex flex-col h-full bg-background transition-transform duration-500 cubic-bezier(0.22, 1, 0.36, 1) ${
          isOpen ? "translate-x-0" : "translate-x-full"
        }`}
      >
        <div className="h-16 flex items-center justify-between px-4 border-b border-border bg-background shrink-0">
          <Link to="/" onClick={onClose} className="flex items-center gap-2">
            <img src={logo} alt="FPF3" className="h-10 w-auto" width={1920} height={1525} />
          </Link>
          <button
            onClick={onClose}
            className="min-w-[44px] min-h-[44px] flex items-center justify-center text-foreground hover:text-primary hover:bg-secondary/50 rounded-sm transition-colors"
            aria-label="Fechar menu"
          >
            <X size={24} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-4 py-6">
          <nav className="flex flex-col gap-1">
            {getMobileNavItems().map((item) => (
              <Link
                key={item.href}
                to={item.href}
                onClick={onClose}
                className={`px-4 py-3.5 text-sm font-bold uppercase tracking-wider transition-all duration-300 hover:bg-secondary/50 rounded-xl min-h-[48px] flex items-center justify-between gap-3 ${
                  location.pathname === item.href
                    ? "text-primary bg-secondary/80 pl-6 border-l-4 border-primary"
                    : item.highlight
                      ? "text-primary bg-primary/10"
                      : "text-foreground/80"
                }`}
              >
                {item.label}
                {item.hint && <span className="text-xs font-semibold normal-case tracking-normal text-muted-foreground">{item.hint}</span>}
              </Link>
            ))}

            <div className="border-t border-border mt-4 pt-4 flex items-center gap-3">
              <UserMenu onClose={onClose} />
            </div>

            <div className="flex flex-col gap-2 mt-4">
              <div onClick={onClose}>
                <HibridoGamesLink variant="button" className="w-full justify-center px-3 py-3 text-xs gap-1" />
              </div>
              <Link
                to="/filiacao"
                onClick={onClose}
                className="bg-primary text-primary-foreground font-bold uppercase tracking-wider text-xs text-center min-h-[48px] flex items-center justify-center px-3 py-2"
              >
                Filiar-se Agora
              </Link>
            </div>
          </nav>
        </div>
      </div>
    </div>,
    document.body
  );
}

export default function Header() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const { role } = useAuth();

  useEffect(() => {
    setIsMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-[9999] transition-all duration-500 ${
          scrolled
            ? "bg-background/95 backdrop-blur-xl border-b border-border/50 shadow-[0_4px_20px_rgba(0,0,0,0.15)]"
            : "bg-background/40 backdrop-blur-md border-b border-border/20"
        }`}
      >
        <div className="container mx-auto px-4">
          <nav className="flex items-center justify-between h-16 md:h-20">
            <Link to="/" className="flex items-center gap-2 shrink-0">
              <img
                src={logo}
                alt="FPF3 - Federação Paraibana de Funcional Fitness"
                className="h-10 sm:h-12 md:h-14 w-auto"
                width={1920}
                height={1525}
              />
            </Link>

            <div className="hidden lg:flex items-center gap-0.5 xl:gap-1.5">
              {getDesktopNavItems().map((item) => (
                <Link
                  key={item.href}
                  to={item.href}
                  className={`relative px-2.5 py-2 text-xs font-bold uppercase tracking-wider whitespace-nowrap transition-all duration-300 hover:text-foreground after:absolute after:bottom-1 after:left-1/2 after:-translate-x-1/2 after:w-0 after:h-[2px] after:bg-primary after:transition-all after:duration-300 hover:after:w-3/4 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-1 rounded-md ${
                    location.pathname === item.href
                      ? "text-primary after:w-3/4"
                      : item.highlight
                        ? "text-primary/90 after:w-3/4"
                        : "text-foreground/60"
                  }`}
                >
                  {item.label}
                </Link>
              ))}
            </div>

            <div className="hidden lg:flex items-center gap-3">
              <UserMenu />
              <HibridoGamesLink variant="button" />
              <Link
                to="/filiacao"
                className="bg-primary hover:bg-primary/90 text-primary-foreground font-bold uppercase tracking-wider text-xs px-6 py-2.5 rounded-lg hover:shadow-[0_0_20px_hsl(0_100%_42%/0.35)] hover:-translate-y-[1px] active:translate-y-0 transition-all duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
              >
                Filiar-se
              </Link>
            </div>

            <button
              onClick={() => setIsMenuOpen(true)}
              className="lg:hidden min-w-[44px] min-h-[44px] flex items-center justify-center text-foreground hover:text-primary transition-colors"
              aria-label="Abrir menu"
            >
              <Menu size={24} />
            </button>
          </nav>
        </div>
      </header>

      <MobileMenu isOpen={isMenuOpen} onClose={() => setIsMenuOpen(false)} />
    </>
  );
}
